import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Calendar,
  MapPin,
  User,
  Mail,
  Phone,
  Check,
  X,
  Loader2,
  Eye,
  Edit2,
  Trash2,
  RefreshCw,
  Shield,
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingEvents, setPendingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });

  useEffect(() => {
    if (user) {
      fetchPendingEvents();
    }
  }, [user]);

  const fetchPendingEvents = async () => {
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const response = await axios.get(`${API_URL}/admin/review-queue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingEvents(response.data.events || []);
      setStats({
        pending: response.data.events?.length || 0,
        approved: response.data.approved_count || 0,
        rejected: response.data.rejected_count || 0
      });
    } catch (error) {
      console.error('Failed to fetch pending events:', error);
      toast.error('Failed to load pending events');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (eventId) => {
    setProcessing(eventId);
    try {
      const token = await user.getIdToken();
      await axios.post(`${API_URL}/admin/approve-event/${eventId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event approved and published!');
      setPendingEvents(prev => prev.filter(e => e.id !== eventId));
      setStats(prev => ({ ...prev, pending: prev.pending - 1, approved: prev.approved + 1 }));
    } catch (error) {
      toast.error('Failed to approve event');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (eventId) => {
    setProcessing(eventId);
    try {
      const token = await user.getIdToken();
      await axios.post(`${API_URL}/admin/reject-event/${eventId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event rejected');
      setPendingEvents(prev => prev.filter(e => e.id !== eventId));
      setStats(prev => ({ ...prev, pending: prev.pending - 1, rejected: prev.rejected + 1 }));
    } catch (error) {
      toast.error('Failed to reject event');
    } finally {
      setProcessing(null);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event.id);
    setEditForm({
      name: event.name || '',
      date: event.date || '',
      location: event.location || '',
      city: event.city || '',
      state: event.state || '',
      description: event.description || '',
      contact_name: event.contact_name || '',
      contact_email: event.contact_email || '',
      contact_phone: event.contact_phone || ''
    });
  };

  const handleSaveEdit = async (eventId) => {
    setProcessing(eventId);
    try {
      const token = await user.getIdToken();
      await axios.put(`${API_URL}/admin/update-event/${eventId}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event updated');
      setPendingEvents(prev => prev.map(e => 
        e.id === eventId ? { ...e, ...editForm } : e
      ));
      setEditingEvent(null);
    } catch (error) {
      toast.error('Failed to update event');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No date';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-bone pt-24 pb-20 flex items-center justify-center">
        <Card className="p-8 text-center">
          <Shield className="w-16 h-16 text-gold mx-auto mb-4" />
          <h2 className="font-display font-bold text-2xl text-deep-green mb-2">Admin Access Required</h2>
          <p className="text-muted-foreground mb-4">Please log in to access the admin panel.</p>
          <Button className="btn-gold" onClick={() => navigate('/login')}>
            Log In
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-24 pb-20">
      {/* Header */}
      <div className="bg-deep-green py-12">
        <div className="container-padding">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-2">Admin Panel</p>
              <h1 className="font-display font-bold text-3xl md:text-4xl text-white" data-testid="admin-title">
                Event Review Queue
              </h1>
            </div>
            <Button 
              variant="outline" 
              className="border-white/30 text-white hover:bg-white/10"
              onClick={fetchPendingEvents}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="container-padding py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 text-amber-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-amber-700">{stats.pending}</p>
              <p className="text-sm text-amber-600">Pending Review</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4 text-center">
              <Check className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
              <p className="text-sm text-green-600">Approved</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-4 text-center">
              <X className="w-6 h-6 text-red-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
              <p className="text-sm text-red-600">Rejected</p>
            </CardContent>
          </Card>
        </div>

        {/* Events List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-fairway" />
          </div>
        ) : pendingEvents.length === 0 ? (
          <Card className="p-12 text-center">
            <Check className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="font-display font-bold text-xl text-deep-green mb-2">All Caught Up!</h3>
            <p className="text-muted-foreground">No events pending review.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {pendingEvents.map((event) => (
              <Card key={event.id} className="overflow-hidden" data-testid={`event-card-${event.id}`}>
                <CardContent className="p-0">
                  {/* Event Header */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                    onClick={() => setExpandedEvent(expandedEvent === event.id ? null : event.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-gold" />
                      </div>
                      <div>
                        <h3 className="font-display font-bold text-lg text-deep-green">{event.name}</h3>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(event.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {event.city}, {event.state}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        Pending
                      </Badge>
                      {expandedEvent === event.id ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedEvent === event.id && (
                    <div className="border-t p-4 bg-gray-50">
                      {editingEvent === event.id ? (
                        /* Edit Form */
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm font-medium text-gray-700">Event Name</label>
                              <Input
                                value={editForm.name}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Date</label>
                              <Input
                                type="date"
                                value={editForm.date}
                                onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">City</label>
                              <Input
                                value={editForm.city}
                                onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">State</label>
                              <Input
                                value={editForm.state}
                                onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Location/Venue</label>
                            <Input
                              value={editForm.location}
                              onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-700">Description</label>
                            <Textarea
                              value={editForm.description}
                              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                              rows={3}
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="text-sm font-medium text-gray-700">Contact Name</label>
                              <Input
                                value={editForm.contact_name}
                                onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Contact Email</label>
                              <Input
                                value={editForm.contact_email}
                                onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                              />
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-700">Contact Phone</label>
                              <Input
                                value={editForm.contact_phone}
                                onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })}
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button 
                              className="btn-gold"
                              onClick={() => handleSaveEdit(event.id)}
                              disabled={processing === event.id}
                            >
                              {processing === event.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                              Save Changes
                            </Button>
                            <Button 
                              variant="outline"
                              onClick={() => setEditingEvent(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* View Details */
                        <div className="space-y-4">
                          <div>
                            <h4 className="font-semibold text-deep-green mb-2">Description</h4>
                            <p className="text-gray-600">{event.description || 'No description provided'}</p>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <h4 className="font-semibold text-deep-green mb-1 flex items-center gap-1">
                                <User className="w-4 h-4" /> Contact
                              </h4>
                              <p className="text-gray-600">{event.contact_name || 'N/A'}</p>
                            </div>
                            <div>
                              <h4 className="font-semibold text-deep-green mb-1 flex items-center gap-1">
                                <Mail className="w-4 h-4" /> Email
                              </h4>
                              <p className="text-gray-600">{event.contact_email || 'N/A'}</p>
                            </div>
                            <div>
                              <h4 className="font-semibold text-deep-green mb-1 flex items-center gap-1">
                                <Phone className="w-4 h-4" /> Phone
                              </h4>
                              <p className="text-gray-600">{event.contact_phone || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="flex gap-2 pt-4 border-t">
                            <Button 
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => handleApprove(event.id)}
                              disabled={processing === event.id}
                              data-testid={`approve-btn-${event.id}`}
                            >
                              {processing === event.id ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <Check className="w-4 h-4 mr-2" />
                              )}
                              Approve & Publish
                            </Button>
                            <Button 
                              variant="outline"
                              onClick={() => handleEdit(event)}
                            >
                              <Edit2 className="w-4 h-4 mr-2" />
                              Edit
                            </Button>
                            <Button 
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleReject(event.id)}
                              disabled={processing === event.id}
                              data-testid={`reject-btn-${event.id}`}
                            >
                              <X className="w-4 h-4 mr-2" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
